/* The two free tiers, in one sentence, and never a number typed into a screen.
 *
 * Its own module rather than a function in `index.tsx`, and the reason is a
 * test: a spec that imports the screen imports its stylesheet too, and the
 * suite's Node transform cannot read CSS. Every pure reader in this app is
 * importable on its own for exactly that reason.
 *
 * Agent V5-C, wave 2.
 *
 * **`next_at` decides which half of the sample sentence is true**, since the
 * final audit of 5 September 2026 (FP1-02). This file read `sample.count`
 * alone, which is the policy number and not the state of this device: a phone
 * whose one free page was spent yesterday read "Try one page free without
 * signing up" for the next thirty days. `api.py:3300` documents the field as
 * "null when one is due now, and a unix time when it is not", which is exactly
 * the branch this used not to take.
 */
import type { Entitlement } from "../../lib/types";

function pagesWord(count: number): string {
  return count === 1 ? "one page" : `${count} pages`;
}

/** The day a sample is due, in the words a person uses.
 *
 *  Unix SECONDS, because that is what the service sends: `sample_next_at`
 *  answers `float | None` and the entitlement carries it through untouched.
 *  Multiplied here rather than declared as a date, so the one place that knows
 *  the unit is the one place that reads it.
 *
 *  The year is added only when it is not this one. Thirty days is the standing
 *  period and a date inside it needs no year, and the period is a setting the
 *  operator can move, so a date that has crossed a new year says which.
 */
function dayOf(seconds: number): string | null {
  const when = new Date(seconds * 1000);
  if (Number.isNaN(when.getTime())) return null;
  const sameYear = when.getFullYear() === new Date().getFullYear();
  return when.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export function sampleLine(ent: Entitlement | null): string | null {
  if (!ent) return null;

  /* The sample the service actually reports for a device with no account.
     `sample.count` is the field the contract names; `credits.granted` is what
     a fresh learner's purse holds on the sample tier, and it is used only
     when the service said which tier this is. */
  const sample = typeof ent.sample?.count === "number"
    ? ent.sample.count
    : ent.tier === "sample" && ent.mode === "credits"
      ? ent.credits?.granted ?? 0
      : 0;

  /* When the next one is due, or null for a device that may have one now. A
     number that is not in the future is treated as "now": the service only
     sends a time it has already decided is ahead of its own clock, and a
     client that renders "comes back on the second" for a date in the past
     would be quoting a clock rather than reading one. */
  const dueAt = typeof ent.sample?.next_at === "number"
    && ent.sample.next_at * 1000 > Date.now()
    ? ent.sample.next_at
    : null;

  const grant = typeof ent.account_grant === "number" ? ent.account_grant : 0;

  const halves: string[] = [];
  if (sample >= 1 && dueAt === null) {
    halves.push(`Try ${pagesWord(sample)} free without signing up.`);
  } else if (sample >= 1) {
    /* The sample is spent. Say when it comes back rather than offering it, and
       say it as a fact about this phone rather than as a refusal: nothing has
       been asked for on the landing screen yet. A time the service sent that
       does not read as a date falls back to the period it stated beside it,
       and to nothing at all where it stated neither. */
    const day = dueAt === null ? null : dayOf(dueAt);
    const period = typeof ent.sample?.period_days === "number"
      ? ent.sample.period_days : null;
    const these = sample === 1 ? "Your free page" : `Your ${sample} free pages`;
    const verb = sample === 1 ? "comes" : "come";
    if (day) halves.push(`${these} ${verb} back on ${day}.`);
    else if (period) halves.push(`${these} ${verb} round every ${period} days.`);
  }
  if (grant >= 1) {
    halves.push(`Sign up for ${grant} free ${grant === 1 ? "page" : "pages"}.`);
  }
  return halves.length > 0 ? halves.join(" ") : null;
}
